import React from 'react'

import { Box, Icon, StatusLabel, Text, dimensions } from '@factorialco/deprecated-design-system'

type StatusColor = 'neutral' | 'info' | 'positive' | 'warning' | 'critical'
type IconColor = 'grey600' | 'viridian800' | 'malibu1000' | 'radicalRed800'

export interface TimelineItemProps {
  /** Unique identifier for the item */
  id: number | string
  /** Main title displayed at the top */
  title: string
  /** Status label text */
  statusLabel: string
  /** Status color type */
  statusType: StatusColor
  /** Color of the timeline circle icon */
  iconColor: IconColor
  /** Whether this is the last item (hides the connecting line) */
  isLast: boolean
  /** Additional content to render below the title and status */
  children?: React.ReactNode
}

/**
 * Individual timeline item component
 */
const TimelineItem = ({
  title,
  statusLabel,
  statusType,
  iconColor,
  isLast,
  children,
}: TimelineItemProps) => {
  return (
    <Box flexDirection="row" width="full">
      {/* Timeline indicator column */}
      <Box alignItems="center" flexDirection="column" width="s24" flexNoShrink>
        {/* Circle icon */}
        <Box paddingTop="s6">
          <Icon.Tiny icon="circle" color={iconColor} />
        </Box>
        {/* Connecting line */}
        {!isLast && (
          <Box
            background="grey300"
            flexGrow
            style={{ width: '2px', marginTop: dimensions.s4, marginBottom: `-${dimensions.s16}` }}
          />
        )}
      </Box>

      {/* Content */}
      <Box flexGrow width="full" gap="s8" paddingLeft="s12">
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          gap="s6"
          flexWrap="wrap"
        >
          <Text.Normal weight="semibold" color="grey1000">
            {title}
          </Text.Normal>
          <StatusLabel type={statusType}>{statusLabel}</StatusLabel>
        </Box>
        {children}
      </Box>
    </Box>
  )
}

export interface VerticalTimelineProps<T> {
  /** Array of items to display in the timeline */
  items: T[]
  /** Function to get the unique key for each item */
  getKey: (item: T) => number | string
  /** Function to get the title for each item */
  getTitle: (item: T) => string
  /** Function to get the status label for each item */
  getStatusLabel: (item: T) => string
  /** Function to get the status color type for each item */
  getStatusType: (item: T) => StatusColor
  /** Function to get the icon color for each item */
  getIconColor: (item: T) => IconColor
  /** Optional function to render custom content for each item */
  renderContent?: (item: T) => React.ReactNode
}

/**
 * Generic vertical timeline component that displays a list of items
 * with status indicators and connecting lines.
 */
export function VerticalTimeline<T>({
  items,
  getKey,
  getTitle,
  getStatusLabel,
  getStatusType,
  getIconColor,
  renderContent,
}: VerticalTimelineProps<T>) {
  return (
    <Box width="full" gap="s16">
      {items.map((item, index) => (
        <TimelineItem
          key={getKey(item)}
          id={getKey(item)}
          title={getTitle(item)}
          statusLabel={getStatusLabel(item)}
          statusType={getStatusType(item)}
          iconColor={getIconColor(item)}
          isLast={index === items.length - 1}
        >
          {renderContent?.(item)}
        </TimelineItem>
      ))}
    </Box>
  )
}
